import type { UpcomingInvoicesReportItem, InvoicesReportItem } from '@vy/types';
import { sortBy } from 'lodash';

export type BillingForecastSummaryRow = {
  customer: { id: string; name: string };
  currency: string;
  monthlyTotals: Map<string, number>;
  total: number;
};

export type BillingForecastBreakdownInvoice = {
  invoice: { id: string; name: string; total: number };
  currency: string;
  monthKey: string;
};

export function buildMonthKey(invoicingDate: Date): string {
  const month = invoicingDate.toLocaleString('en-US', { month: 'short' });
  const year = invoicingDate.getFullYear();
  return `${month} ${year}`;
}

export function buildBillingForecastSummary(
  items: UpcomingInvoicesReportItem[],
): BillingForecastSummaryRow[] {
  const byCustomerId = groupItemsByCustomer(items);
  const summaryRows: BillingForecastSummaryRow[] = [];

  for (const [customerId, customerItems] of byCustomerId) {
    const firstItem = customerItems[0];
    const currency = firstItem.revenue.currency;
    const monthlyTotals = buildCustomerMonthlyTotals(customerItems);
    const total = customerItems.reduce((sum, item) => sum + item.revenue.total, 0);

    summaryRows.push({
      customer: { id: customerId, name: firstItem.customer.name },
      currency,
      monthlyTotals,
      total,
    });
  }

  return sortBy(summaryRows, (row) => row.customer.name.toLowerCase());
}

export function buildBillingForecastBreakdownByCustomer(
  items: UpcomingInvoicesReportItem[],
): Map<string, BillingForecastBreakdownInvoice[]> {
  const byCustomerId = new Map<string, BillingForecastBreakdownInvoice[]>();

  for (const item of items) {
    const customerId = item.customer.id;
    const existing = byCustomerId.get(customerId) ?? [];

    for (const invoice of item.invoices) {
      existing.push({
        invoice: { id: invoice.id, name: invoice.name, total: invoice.total },
        currency: item.revenue.currency,
        monthKey: buildMonthKey(invoice.invoicingDate),
      });
    }

    byCustomerId.set(customerId, existing);
  }

  return byCustomerId;
}

function groupItemsByCustomer(
  items: UpcomingInvoicesReportItem[],
): Map<string, UpcomingInvoicesReportItem[]> {
  const map = new Map<string, UpcomingInvoicesReportItem[]>();

  for (const item of items) {
    const customerId = item.customer.id;
    const existing = map.get(customerId) ?? [];
    existing.push(item);
    map.set(customerId, existing);
  }

  return map;
}

function buildCustomerMonthlyTotals(
  customerItems: UpcomingInvoicesReportItem[],
): Map<string, number> {
  const monthlyTotals = new Map<string, number>();

  for (const item of customerItems) {
    for (const invoice of item.invoices) {
      accumulateInvoiceToMonthlyTotals(monthlyTotals, invoice);
    }
  }

  return monthlyTotals;
}

function accumulateInvoiceToMonthlyTotals(
  monthlyTotals: Map<string, number>,
  invoice: InvoicesReportItem,
): void {
  const monthKey = buildMonthKey(invoice.invoicingDate);
  const existing = monthlyTotals.get(monthKey) ?? 0;
  monthlyTotals.set(monthKey, existing + invoice.total);
}
